/* RESPONSIBLE TEAM: team-reporting */

import {
  ChartSchema,
  ChartDefinition,
  Timezone,
  DateRange,
} from '@/features/report-snapshots/api/schema';
import { useLingui } from '@lingui/react/macro';
import type { ChartType } from '@/features/report-snapshots/types';
import { DonutChart } from '@/features/report-snapshots/components/charts/donut-chart';
import { CounterChart } from '@/features/report-snapshots/components/charts/counter-chart';
import { SeriesChart } from '@/features/report-snapshots/components/charts/series-chart';
import { HeatmapChart } from '@/features/report-snapshots/components/charts/heatmap-chart';
import { Banner } from '@/features/report-snapshots/components/charts/banner';
import { EmptyStateChart } from '@/features/report-snapshots/components/charts/empty-state-chart';
import { ChartTitle } from '@/features/report-snapshots/components/charts/chart-title';
import { isEmptyChart } from '@/features/report-snapshots/utils/chart-data-helpers';
import { isCounterChart } from '@/features/report-snapshots/utils/common';

interface BaseChartProps {
  chartType: ChartType;
  chartSchemas: ChartSchema[];
  chartDefinition: ChartDefinition;
  timezone: Timezone;
  dateRange: DateRange;
}

export function BaseChart({
  chartType,
  chartSchemas,
  chartDefinition,
  timezone,
  dateRange,
}: BaseChartProps) {
  const { i18n } = useLingui();

  if (isCounterChart(chartType)) {
    return (
      <div className="flex-1 min-h-0">
        <CounterChart
          chartSchemas={chartSchemas}
          chartDefinition={chartDefinition}
          dateRange={dateRange}
          timezone={timezone}
        />
      </div>
    );
  }

  if (isEmptyChart(chartSchemas, chartType)) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        <ChartTitle chartSchema={chartSchemas[0]} chartDefinition={chartDefinition} />
        <EmptyStateChart />
      </div>
    );
  }

  switch (chartType) {
    case 'pie':
      return (
        <div className="flex-1 min-h-0">
          <DonutChart
            chartSchema={chartSchemas[0]}
            chartDefinition={chartDefinition}
            timezone={timezone}
            i18n={i18n}
          />
        </div>
      );
    case 'heatmap':
      return (
        <div className="flex-1 min-h-0">
          <HeatmapChart
            chartSchema={chartSchemas[0]}
            chartDefinition={chartDefinition}
            timezone={timezone}
            i18n={i18n}
          />
        </div>
      );
    case 'banner':
      return <Banner chartSchema={chartSchemas[0]} chartDefinition={chartDefinition} />;
    default: {
      return (
        <SeriesChart
          chartSchemas={chartSchemas}
          chartDefinition={chartDefinition}
          timezone={timezone}
          i18n={i18n}
          chartType={chartType}
          dateRange={dateRange}
        />
      );
    }
  }
}
